/**
 * FG Magnit — product details: tab row with scroll-spy.
 *
 * On phones the panels stay Dawn accordions and this does nothing beyond
 * leaving them alone. From 990px up every panel is held open and laid out
 * as a section with a heading; the tab row sticks to the top and the tab
 * for the section under it is marked current as the page scrolls. Clicking
 * a tab scrolls to that section and moves focus to its heading.
 *
 * The tabs are plain anchors to the <details> ids, so with JavaScript off
 * they still jump to the right place.
 */
class FgProductDetails extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.fg-pdetails__tabs');
    this.tabs = Array.from(this.querySelectorAll('.fg-pdetails__tab'));
    this.panels = this.tabs
      .map((tab) => this.querySelector(tab.getAttribute('href')))
      .filter(Boolean);
    if (!this.bar || this.panels.length === 0) return;

    this.desktop = window.matchMedia('(min-width: 990px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.header = document.querySelector('.section-header');

    this.tabs.forEach((tab, i) => tab.addEventListener('click', (event) => this.onTab(event, i)));
    this.panels.forEach((panel) => {
      const summary = panel.querySelector('summary');
      if (summary) summary.addEventListener('click', (event) => this.onSummary(event));
    });

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.placeBar();
        this.spy();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Dawn's header slides back in when the shopper scrolls up; the tab row
    // then has to sit under it rather than behind it.
    if (this.header && 'MutationObserver' in window) {
      this.headerWatch = new MutationObserver(() => this.placeBar());
      this.headerWatch.observe(this.header, { attributes: true, attributeFilter: ['class'] });
    }

    this.onMedia = () => this.layout();
    this.desktop.addEventListener('change', this.onMedia);
    this.layout();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.desktop) this.desktop.removeEventListener('change', this.onMedia);
    if (this.headerWatch) this.headerWatch.disconnect();
  }

  /** Desktop: every panel open. Phones: back to the editor's open/closed choice. */
  layout() {
    if (this.desktop.matches) {
      this.panels.forEach((panel) => {
        panel.open = true;
      });
      this.placeBar();
      this.spy();
    } else {
      this.panels.forEach((panel) => {
        panel.open = panel.hasAttribute('data-open');
      });
      this.tabs.forEach((tab) => this.mark(tab, false));
    }
  }

  /** Height of whatever is stuck above the tab row right now. */
  headerOffset() {
    if (!this.header) return 0;
    const stuck = this.header.classList.contains('shopify-section-header-sticky');
    const hidden = this.header.classList.contains('shopify-section-header-hidden');
    return stuck && !hidden ? this.header.getBoundingClientRect().height : 0;
  }

  placeBar() {
    if (!this.desktop.matches) return;
    const top = Math.round(this.headerOffset());
    if (top !== this.barTop) {
      this.barTop = top;
      this.style.setProperty('--fg-tabs-top', `${top}px`);
    }
  }

  /** Distance from the viewport top to the bottom edge of the tab row. */
  line() {
    return this.headerOffset() + this.bar.getBoundingClientRect().height + 24;
  }

  spy() {
    if (!this.desktop.matches) return;
    const line = this.line();
    let current = 0;
    this.panels.forEach((panel, i) => {
      if (panel.getBoundingClientRect().top <= line) current = i;
    });

    // Reaching the foot of the page counts as reaching the last section,
    // which may otherwise be too short ever to cross the line.
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    if (atBottom) current = this.panels.length - 1;

    this.tabs.forEach((tab, i) => this.mark(tab, i === current));
  }

  mark(tab, on) {
    tab.classList.toggle('is-active', on);
    if (on) tab.setAttribute('aria-current', 'true');
    else tab.removeAttribute('aria-current');
  }

  onTab(event, index) {
    const panel = this.panels[index];
    if (!panel) return;

    if (!this.desktop.matches) {
      // Phones: let the anchor jump, but make sure the accordion is open.
      panel.open = true;
      return;
    }

    event.preventDefault();
    const target = panel.getBoundingClientRect().top + window.scrollY - this.line() + 24;
    window.scrollTo({ top: target, behavior: this.reduced.matches ? 'auto' : 'smooth' });
    if (history.replaceState) history.replaceState(null, '', this.tabs[index].getAttribute('href'));

    const heading = panel.querySelector('.accordion__title');
    if (heading) heading.focus({ preventScroll: true });
    this.tabs.forEach((tab, i) => this.mark(tab, i === index));
  }

  /** On desktop the headings are labels, not toggles. */
  onSummary(event) {
    if (this.desktop.matches) event.preventDefault();
  }
}

if (!customElements.get('fg-product-details')) {
  customElements.define('fg-product-details', FgProductDetails);
}
